import { isValid } from "date-fns";

import { ServiceTeam } from "@fartherfinance/frontend/api/Entity/requests/getServiceTeams";
import {
  AssigneeType,
  TaskAssignee,
  TaskStatus,
  TaskType,
  TaskTypeV2,
} from "@fartherfinance/frontend/api/Tasks/Types";
import { isGroupAssignee } from "@fartherfinance/frontend/api/Tasks/utils/isGroupAssignee";
import { ServiceTeamId } from "@fartherfinance/frontend/api/Types";

import { taskFiltersStorageKey } from "@src/constants/localStorageKeys";
import {
  EnhancedAutocompleteOption,
  UNKNOWN_GROUP,
  UNKNOWN_USER,
} from "@src/multiCustodian/pages/Advisor/common";

import {
  TaskAssigneeAutocompleteOption,
  TASKS_DEFAULT_QUERY_PARAMS,
} from "./common";
import { resetTabFilters } from "./storage";

export const mapSerivceTeamToOption = (
  serviceTeam: ServiceTeam
): EnhancedAutocompleteOption<ServiceTeamId> => {
  return {
    label: serviceTeam.name,
    value: serviceTeam.serviceTeamId,
  };
};

const findFirstLocalServiceTeam = (
  serviceTeams: ServiceTeam[]
): ServiceTeam | undefined => {
  return serviceTeams.find((team) => team.type === "Regular");
};

const findTradingGlobalServiceTeam = (
  serviceTeams: ServiceTeam[]
): ServiceTeam | undefined => {
  return serviceTeams.find(
    (team) => team.type === "Global" && team.name.includes("Trading")
  );
};

const findPlanningServiceTeam = (
  serviceTeams: ServiceTeam[]
): ServiceTeam | undefined => {
  return serviceTeams.find(
    (team) => team.type === "Global" && team.name.includes("Planning")
  );
};

const findProductServiceTeam = (
  serviceTeams: ServiceTeam[]
): ServiceTeam | undefined => {
  return serviceTeams.find(
    (team) => team.type === "Global" && team.name.includes("Product")
  );
};

const findBillingServiceTeam = (
  serviceTeams: ServiceTeam[]
): ServiceTeam | undefined => {
  return serviceTeams.find(
    (team) => team.type === "Global" && team.name.includes("Billing")
  );
};

export const resolveServiceTeam = (
  serviceTeams: ServiceTeam[],
  taskType: TaskType | TaskTypeV2 | undefined,
  isServiceTeamFieldVisibleForCX: boolean
): ServiceTeam | undefined => {
  if (taskType === TaskTypeV2.enum.TRADE_REQUEST) {
    return findTradingGlobalServiceTeam(serviceTeams);
  }

  if (
    taskType === TaskTypeV2.enum.FINANCIAL_PLANNING ||
    taskType === TaskTypeV2.enum.IRA_ROLLOVERS
  ) {
    return findPlanningServiceTeam(serviceTeams);
  }

  if (taskType === TaskTypeV2.enum.BUGS_AND_ENGINEERING_REQUESTS) {
    return findProductServiceTeam(serviceTeams);
  }

  if (taskType === TaskTypeV2.enum.BILLING) {
    return findBillingServiceTeam(serviceTeams);
  }

  // NOTE: for none CX users service team field is hidden, but we still need to send some value to the backend
  if (!isServiceTeamFieldVisibleForCX) {
    return findFirstLocalServiceTeam(serviceTeams);
  }

  return undefined;
};

export const mapAssigneeToOption = (
  assignee: TaskAssignee
): TaskAssigneeAutocompleteOption => {
  return isGroupAssignee(assignee)
    ? {
        label: assignee.groupName ?? UNKNOWN_GROUP,
        value: assignee.id,
        meta: { type: AssigneeType.enum.GROUP },
      }
    : {
        label: assignee.personName
          ? `${assignee.personName.first} ${assignee.personName.last}`
          : UNKNOWN_USER,
        value: assignee.id,
        meta: { type: AssigneeType.enum.PERSON },
      };
};

export const isDateValid = (date: Date | null): boolean | string => {
  return date && !isValid(date) ? "Invalid date" : true;
};

type TaskEditableField =
  | "taskStatus"
  | "taskPriority"
  | "taskType"
  | "title"
  | "description"
  | "serviceTeam"
  | "assignee"
  | "client"
  | "advisor"
  | "dueDate"
  | "custodian";

export const systemGeneratedAllowList: TaskEditableField[] = [
  "taskStatus",
  "taskPriority",
  "serviceTeam",
  "assignee",
  "title",
  "dueDate",
];

const taskTerminalStatuses: TaskStatus[] = [
  TaskStatus.enum.COMPLETED,
  TaskStatus.enum.CANCELED,
];

export const isTaskFieldEditingRestricted = (
  fieldName: TaskEditableField,
  task: { taskStatus: TaskStatus; isSystemGenerated: boolean } | undefined
): boolean => {
  if (!task) {
    return false;
  }

  const isOutOfAllowList =
    systemGeneratedAllowList.includes(fieldName) === false;

  // NOTE: order matters, we need to list the restrictions from the most broad to the most specific

  // NOTE: if task is in terminal status, only taskStatus can be edited
  if (
    taskTerminalStatuses.includes(task.taskStatus) &&
    fieldName !== "taskStatus"
  ) {
    return true;
  }

  // NOTE: if task is system generated, only fields from allow list can be edited
  if (task.isSystemGenerated && isOutOfAllowList) {
    return true;
  }

  return false;
};

/**
 * Reset task filters to default values in both global localStorage and tab-specific sessionStorage.
 * Useful for cases where you need to reset filters outside of the context.
 * Returns a boolean indicating success or failure.
 */
export function resetTaskFilters(): boolean {
  try {
    // Create a deep copy of default params to avoid reference issues
    const defaultParams = JSON.parse(
      JSON.stringify(TASKS_DEFAULT_QUERY_PARAMS)
    );

    // Reset global v3 filters (backward compatibility)
    localStorage.setItem(
      `${taskFiltersStorageKey}_v3`,
      JSON.stringify(defaultParams)
    );

    // Reset tab-specific filters
    resetTabFilters(defaultParams);

    // Verify global storage was updated (for backward compatibility)
    const v3Filters = localStorage.getItem(`${taskFiltersStorageKey}_v3`);

    if (!v3Filters) {
      console.error("Failed to verify global task filters reset");
      return false;
    }

    return true;
  } catch (error) {
    console.error("Error resetting task filters:", error);
    return false;
  }
}
